import React, { useState, useEffect, useCallback } from 'react';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Play, CheckCircle, XCircle, AlertTriangle, Clock, Loader2,
  RefreshCw, Server, Monitor, Shield, ChevronRight, Search,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  COMPLETED: 'bg-green-100 text-green-700', COMPLETED_WITH_WARNINGS: 'bg-amber-100 text-amber-700',
  FAILED: 'bg-red-100 text-red-700', REJECTED: 'bg-gray-100 text-gray-500',
  CANCELLED: 'bg-gray-100 text-gray-500', EXECUTING: 'bg-blue-100 text-blue-700',
  VERIFYING: 'bg-blue-100 text-blue-700', AWAITING_APPROVAL: 'bg-amber-100 text-amber-700',
  APPROVED: 'bg-green-100 text-green-700', REQUESTED: 'bg-blue-100 text-blue-700',
};

// ── Batch Creation Dialog ────────────────────────────────────────────

export function BulkActionDialog({ selectedAssets, onClose, onCreated }) {
  const [actionType, setActionType] = useState('');
  const [reason, setReason] = useState('');
  const [params, setParams] = useState({});
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const ACTIONS = [
    { type: 'RUN_SCRIPT', label: 'Run Script', needsScript: true },
    { type: 'REBOOT_DEVICE', label: 'Reboot' },
    { type: 'SERVICE_RESTART', label: 'Restart Service', needsService: true },
    { type: 'SERVICE_START', label: 'Start Service', needsService: true },
    { type: 'SERVICE_STOP', label: 'Stop Service', needsService: true },
    { type: 'PATCH_SCAN', label: 'Patch Scan', needsCategory: true },
    { type: 'PATCH_APPLY', label: 'Patch Apply', needsCategory: true },
  ];

  const loadPreview = async () => {
    if (!actionType || selectedAssets.length === 0) return;
    setLoading(true);
    try {
      const r = await api.post('/rmm/batches', {
        action_type: actionType,
        asset_ids: selectedAssets.map(a => a.id),
        parameters: params,
        reason: reason.trim() || 'Preview request',
      });
      setPreview(r.data);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Preview failed');
    }
    setLoading(false);
  };

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error('Reason required (min 5 chars)'); return; }
    setSubmitting(true);
    try {
      const r = await api.post('/rmm/batches', {
        action_type: actionType,
        asset_ids: selectedAssets.map(a => a.id),
        parameters: params,
        reason: reason.trim(),
      });
      toast.success(`Batch created: ${r.data.status}`);
      if (onCreated) onCreated(r.data);
      onClose();
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Batch creation failed');
    }
    setSubmitting(false);
  };

  const selectedAction = ACTIONS.find(a => a.type === actionType);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center overflow-auto" onClick={onClose}>
      <Card className="w-full max-w-2xl mx-4 my-8 max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()} data-testid="bulk-action-dialog">
        <CardHeader>
          <CardTitle>Bulk Action — {selectedAssets.length} endpoints selected</CardTitle>
          <CardDescription>All targets are validated server-side before execution</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Action Selection */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {ACTIONS.map(a => (
              <Button key={a.type} variant={actionType === a.type ? 'default' : 'outline'} size="sm"
                onClick={() => { setActionType(a.type); setPreview(null); }} data-testid={`bulk-${a.type}`}>
                {a.label}
              </Button>
            ))}
          </div>

          {/* Parameters */}
          {selectedAction?.needsService && (
            <Input placeholder="Service name (exact)" value={params.service_name || ''}
              onChange={e => setParams({ ...params, service_name: e.target.value })} />
          )}
          {selectedAction?.needsCategory && (
            <div className="flex gap-2">
              {['OS', 'SOFTWARE'].map(c => (
                <Button key={c} variant={(params.patch_category || 'OS') === c ? 'default' : 'outline'} size="sm"
                  onClick={() => setParams({ ...params, patch_category: c })}>{c}</Button>
              ))}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-sm font-medium">Reason *</label>
            <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why is this action needed?"
              maxLength={500} data-testid="bulk-reason" />
          </div>

          {/* Reboot Warning */}
          {(actionType === 'REBOOT_DEVICE' || actionType === 'PATCH_APPLY') && (
            <div className="bg-amber-50 border border-amber-200 rounded p-2 text-sm text-amber-800">
              ⚠️ {actionType === 'REBOOT_DEVICE' ? 'Endpoints will be rebooted.' : 'Reboot behavior is controlled by NinjaOne patch policy and may occur.'}
            </div>
          )}

          {/* Preview */}
          {preview && (
            <Card className="bg-gray-50">
              <CardContent className="p-3 space-y-2">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div><span className="text-gray-500">Eligible:</span> <strong>{preview.authorized_target_count}</strong></div>
                  <div><span className="text-gray-500">Excluded:</span> <strong>{preview.excluded_target_count}</strong></div>
                  <div><span className="text-gray-500">Risk:</span> <Badge variant="custom" className={preview.risk_level === 'HIGH' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>{preview.risk_level}</Badge></div>
                </div>
                {preview.approval_required && <Badge variant="custom" className="bg-amber-100 text-amber-700">Approval required</Badge>}
                {preview.exclusions?.length > 0 && (
                  <div className="text-xs text-gray-500">
                    Excluded: {preview.exclusions.map((e, i) => <span key={i} className="block">{e.hostname || e.asset_id}: {e.reason}</span>)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {!preview && actionType && (
              <Button onClick={loadPreview} disabled={loading} className="flex-1" data-testid="bulk-preview">
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Preview
              </Button>
            )}
            {preview && (
              <Button onClick={submit} disabled={submitting || reason.trim().length < 5} className="flex-1" variant="default"
                data-testid="bulk-submit">
                {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Play className="h-4 w-4 mr-2" />}
                Submit Batch ({preview.authorized_target_count} targets)
              </Button>
            )}
            <Button variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Approval Queue ───────────────────────────────────────────────────

export function ApprovalQueue() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [actions, batches] = await Promise.all([
        api.get('/rmm/actions?limit=50'),
        api.get('/rmm/batches?limit=50'),
      ]);
      const pending = [
        ...(actions.data || []).filter(a => a.status === 'AWAITING_APPROVAL').map(a => ({ ...a, _type: 'action' })),
        ...(batches.data || []).filter(b => b.status === 'AWAITING_APPROVAL').map(b => ({ ...b, _type: 'batch' })),
      ].sort((a, b) => (b.requested_at || '').localeCompare(a.requested_at || ''));
      setItems(pending);
    } catch (e) { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); const i = setInterval(load, 30000); return () => clearInterval(i); }, [load]);

  const handleApprove = async (item) => {
    try {
      const endpoint = item._type === 'batch'
        ? `/rmm/batches/${item.batch_id}/approve`
        : `/rmm/actions/${item.action_id}/approve`;
      await api.post(endpoint, {});
      toast.success('Approved');
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Approval failed');
    }
  };

  const handleReject = async (item) => {
    try {
      const endpoint = item._type === 'batch'
        ? `/rmm/batches/${item.batch_id}/reject`
        : `/rmm/actions/${item.action_id}/reject`;
      await api.post(endpoint, {});
      toast.success('Rejected');
      load();
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Rejection failed');
    }
  };

  if (loading) return <div className="flex justify-center py-4"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>;
  if (items.length === 0) return <p className="text-center text-gray-400 text-sm py-4">No pending approvals</p>;

  return (
    <div className="space-y-2" data-testid="approval-queue">
      {items.map((item, i) => (
        <Card key={i} className="p-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="font-medium text-sm">{item.action_type || item._type}</span>
              <span className="text-xs text-gray-500 ml-2">by {item.actor_email || item.actor_user_id || '?'}</span>
              {item._type === 'batch' && <Badge variant="custom" className="ml-2 bg-blue-100 text-blue-700">{item.authorized_target_count} targets</Badge>}
              <Badge variant="custom" className="ml-2 bg-red-100 text-red-700">{item.risk_level}</Badge>
            </div>
            <div className="flex gap-1">
              <Button size="sm" onClick={() => handleApprove(item)} data-testid={`approve-${i}`}>Approve</Button>
              <Button size="sm" variant="outline" onClick={() => handleReject(item)}>Reject</Button>
            </div>
          </div>
          {item.reason && <p className="text-xs text-gray-500 mt-1">{item.reason}</p>}
        </Card>
      ))}
    </div>
  );
}

// ── Action/Batch History ─────────────────────────────────────────────

export function RmmActionHistory() {
  const [actions, setActions] = useState([]);
  const [batches, setBatches] = useState([]);
  const [tab, setTab] = useState('actions');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/rmm/actions?limit=50'),
      api.get('/rmm/batches?limit=20'),
    ]).then(([a, b]) => {
      setActions(a.data || []);
      setBatches(b.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = (tab === 'actions' ? actions : batches).filter(item => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (item.action_type || '').toLowerCase().includes(s) ||
           (item.actor_email || '').toLowerCase().includes(s) ||
           (item.status || '').toLowerCase().includes(s);
  });

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin" /></div>;

  return (
    <div className="space-y-3" data-testid="rmm-action-history">
      <div className="flex items-center gap-2">
        <Button variant={tab === 'actions' ? 'default' : 'outline'} size="sm" onClick={() => setTab('actions')}>Actions</Button>
        <Button variant={tab === 'batches' ? 'default' : 'outline'} size="sm" onClick={() => setTab('batches')}>Batches</Button>
        <div className="relative flex-1">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
          <Input placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8" />
        </div>
      </div>

      <Table>
        <TableHeader><TableRow>
          <TableHead>Type</TableHead><TableHead>Actor</TableHead><TableHead>Status</TableHead>
          <TableHead>Requested</TableHead>{tab === 'batches' && <TableHead>Targets</TableHead>}
        </TableRow></TableHeader>
        <TableBody>
          {filtered.map((item, i) => (
            <TableRow key={i}>
              <TableCell className="text-sm font-medium">{item.action_type}</TableCell>
              <TableCell className="text-xs text-gray-500">{item.actor_email || '?'}</TableCell>
              <TableCell><Badge variant="custom" className={STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-500'}>{item.status}</Badge></TableCell>
              <TableCell className="text-xs text-gray-500">{item.requested_at ? new Date(item.requested_at).toLocaleString() : ''}</TableCell>
              {tab === 'batches' && <TableCell className="text-sm">
                {item.successful_count || 0}/{item.authorized_target_count || 0}
                {item.failed_count > 0 && <span className="text-red-500 ml-1">({item.failed_count} failed)</span>}
              </TableCell>}
            </TableRow>
          ))}
          {filtered.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-gray-400">No records</TableCell></TableRow>}
        </TableBody>
      </Table>
    </div>
  );
}
